import React, { Component, ReactNode } from 'react';
import {
  Alert, AlertColor, AlertTitle, Box, Button, Grow,
  LinearProgress, Portal, Stack, Typography,
} from '@mui/material';

export type NotificationSeverity = 'success' | 'info' | 'warning' | 'error';
export type NotificationPlacement =
  | 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left'
  | 'top-center' | 'bottom-center';

export interface IAppNotification {
  id?: number | string;
  severity?: NotificationSeverity;
  title?: string;
  message: ReactNode;
  details?: ReactNode;
  timestamp?: string;
  duration?: number | null;
  priority?: boolean;
  icon?: ReactNode;
  actionLabel?: string;
  onAction?: () => void;
  onClick?: () => void;
  disableClose?: boolean;
}

export interface INotificationCenterProps {
  maxNotifications?: number;
  placement?: NotificationPlacement;
  defaultDuration?: number;
  width?: number | string;
  zIndex?: number;
}

interface IRenderedNotification extends IAppNotification {
  id: number | string;
  severity: NotificationSeverity;
  open: boolean;
  createdAt: number;
}

interface INotificationCenterState {
  notifications: IRenderedNotification[];
}

const EXIT_DURATION = 220;

export default class NotificationCenter extends Component<
  INotificationCenterProps,
  INotificationCenterState
> {
  static defaultProps: Partial<INotificationCenterProps> = {
    maxNotifications: 4,
    placement: 'top-right',
    defaultDuration: 6000,
    width: 390,
  };

  private static instance: NotificationCenter | null = null;
  private static pending: IAppNotification[] = [];
  private static sequence = 1;
  private timers = new Map<number | string, number>();

  state: INotificationCenterState = { notifications: [] };

  componentDidMount(): void {
    NotificationCenter.instance = this;
    const pending = [...NotificationCenter.pending];
    NotificationCenter.pending = [];
    pending.forEach(this.enqueue);
  }

  componentWillUnmount(): void {
    if (NotificationCenter.instance === this) NotificationCenter.instance = null;
    this.timers.forEach(window.clearTimeout);
    this.timers.clear();
  }

  static showNotification(notification: IAppNotification): number | string;
  static showNotification(
    severity: NotificationSeverity,
    message: ReactNode,
    title?: string,
  ): number | string;
  static showNotification(
    notificationOrSeverity: IAppNotification | NotificationSeverity,
    message?: ReactNode,
    title?: string,
  ): number | string {
    const notification: IAppNotification =
      typeof notificationOrSeverity === 'string'
        ? { severity: notificationOrSeverity, message: message ?? '', title }
        : notificationOrSeverity;

    const id = notification.id ?? NotificationCenter.sequence++;
    const prepared = { ...notification, id };

    if (NotificationCenter.instance) NotificationCenter.instance.enqueue(prepared);
    else NotificationCenter.pending.push(prepared);

    return id;
  }

  static success(message: ReactNode, title?: string): number | string {
    return this.showNotification('success', message, title);
  }
  static info(message: ReactNode, title?: string): number | string {
    return this.showNotification('info', message, title);
  }
  static warning(message: ReactNode, title?: string): number | string {
    return this.showNotification('warning', message, title);
  }
  static error(message: ReactNode, title?: string): number | string {
    return this.showNotification('error', message, title);
  }
  static dismiss(id: number | string): void {
    NotificationCenter.instance?.dismiss(id);
  }
  static clear(): void {
    NotificationCenter.pending = [];
    NotificationCenter.instance?.clearAll();
  }

  private enqueue = (notification: IAppNotification): void => {
    const item: IRenderedNotification = {
      ...notification,
      id: notification.id ?? NotificationCenter.sequence++,
      severity: notification.severity ?? 'info',
      open: true,
      createdAt: Date.now(),
    };

    this.setState(
      ({ notifications }) => ({
        notifications: this.cap([...notifications, item]),
      }),
      () => this.schedule(item),
    );
  };

  private cap = (items: IRenderedNotification[]): IRenderedNotification[] => {
    const sorted = [...items].sort((a, b) => {
      const priority = Number(Boolean(b.priority)) - Number(Boolean(a.priority));
      return priority || b.createdAt - a.createdAt;
    });
    const max = this.props.maxNotifications ?? 4;
    if (!Number.isFinite(max)) return sorted;
    const retained = sorted.slice(0, max);
    const ids = new Set(retained.map((item) => item.id));
    items.filter((item) => !ids.has(item.id)).forEach((item) => this.clearTimer(item.id));
    return retained;
  };

  private duration = (item: IRenderedNotification): number | null => {
    if (item.duration === null || item.duration === 0) return null;
    return item.duration ?? this.props.defaultDuration ?? 6000;
  };

  private schedule = (item: IRenderedNotification): void => {
    const duration = this.duration(item);
    if (!duration) return;
    this.clearTimer(item.id);
    this.timers.set(item.id, window.setTimeout(() => this.dismiss(item.id), duration));
  };

  private clearTimer = (id: number | string): void => {
    const timer = this.timers.get(id);
    if (timer !== undefined) window.clearTimeout(timer);
    this.timers.delete(id);
  };

  private dismiss = (id: number | string): void => {
    this.clearTimer(id);
    this.setState(
      ({ notifications }) => ({
        notifications: notifications.map((item) =>
          item.id === id ? { ...item, open: false } : item,
        ),
      }),
      () => window.setTimeout(() => {
        this.setState(({ notifications }) => ({
          notifications: notifications.filter((item) => item.id !== id),
        }));
      }, EXIT_DURATION),
    );
  };

  private clearAll = (): void => {
    this.timers.forEach(window.clearTimeout);
    this.timers.clear();
    this.setState({ notifications: [] });
  };

  private placementSx = (): object => {
    const placement = this.props.placement ?? 'top-right';
    return {
      top: placement.startsWith('top') ? 20 : undefined,
      bottom: placement.startsWith('bottom') ? 20 : undefined,
      left: placement.endsWith('left') ? 20 : placement.endsWith('center') ? '50%' : undefined,
      right: placement.endsWith('right') ? 20 : undefined,
      transform: placement.endsWith('center') ? 'translateX(-50%)' : undefined,
    };
  };

  private renderItem = (item: IRenderedNotification): ReactNode => {
    const duration = this.duration(item);
    const action = item.actionLabel && item.onAction ? (
      <Button
        color="inherit"
        size="small"
        onClick={(event) => {
          event.stopPropagation();
          item.onAction?.();
          this.dismiss(item.id);
        }}
        sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
      >
        {item.actionLabel}
      </Button>
    ) : undefined;

    return (
      <Grow key={item.id} in={item.open} timeout={{ enter: 260, exit: EXIT_DURATION }}>
        <Box sx={{ width: '100%' }}>
          <Alert
            severity={item.severity as AlertColor}
            variant="filled"
            icon={item.icon}
            action={action}
            onClose={item.disableClose ? undefined : () => this.dismiss(item.id)}
            onClick={() => {
              if (!item.onClick) return;
              item.onClick();
              this.dismiss(item.id);
            }}
            elevation={8}
            sx={{
              position: 'relative',
              overflow: 'hidden',
              alignItems: 'flex-start',
              borderRadius: 2.5,
              cursor: item.onClick ? 'pointer' : 'default',
              boxShadow: '0 14px 38px rgba(0,0,0,.20)',
              '& .MuiAlert-message': { width: '100%', minWidth: 0, py: 0.25 },
              '& .MuiAlert-action': { alignItems: 'flex-start', pt: 0.25 },
            }}
          >
            {item.title && <AlertTitle sx={{ mb: 0.25, fontWeight: 750 }}>{item.title}</AlertTitle>}
            <Typography component="div" variant="body2" sx={{ fontWeight: item.title ? 400 : 600, lineHeight: 1.45 }}>
              {item.message}
            </Typography>
            {item.details && (
              <Typography component="div" variant="caption" sx={{ display: 'block', mt: 0.75, opacity: 0.9 }}>
                {item.details}
              </Typography>
            )}
            {item.timestamp && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.75, opacity: 0.72 }}>
                {item.timestamp}
              </Typography>
            )}
            {duration && (
              <LinearProgress
                variant="determinate"
                value={100}
                aria-hidden
                sx={{
                  position: 'absolute', left: 0, right: 0, bottom: 0, height: 3,
                  bgcolor: 'rgba(255,255,255,.20)',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: 'rgba(255,255,255,.72)',
                    transformOrigin: 'left center',
                    animation: `notification-countdown ${duration}ms linear forwards`,
                  },
                  '@keyframes notification-countdown': {
                    from: { transform: 'translateX(0%)' },
                    to: { transform: 'translateX(-100%)' },
                  },
                }}
              />
            )}
          </Alert>
        </Box>
      </Grow>
    );
  };

  render(): ReactNode {
    return (
      <Portal>
        <Stack
          role="region"
          aria-label="Application notifications"
          spacing={1.25}
          sx={{
            position: 'fixed',
            zIndex: this.props.zIndex ?? ((theme) => theme.zIndex.snackbar + 20),
            width: this.props.width ?? 390,
            maxWidth: 'calc(100vw - 32px)',
            pointerEvents: 'none',
            ...this.placementSx(),
            '& > *': { pointerEvents: 'auto' },
          }}
        >
          {this.state.notifications.map(this.renderItem)}
        </Stack>
      </Portal>
    );
  }
}
